import React from 'react'
import { useNewTranslation } from 'hooks'
import PaymentIcon from './PaymentIcon'
import Text from 'particles/Text'
import styled from 'particles/themes/styled'
import { Theme } from 'particles/themes'
import { PaymentIconType } from './PaymentIcon'

interface PaymentCardProps {
    onClick?: () => void
    title: string
    description: string
    subTitle?: string
    isDisabled?: boolean
    isSelected?: boolean
    icon: PaymentIconType
    isRecommended?: boolean
    dataTestId?: string
}

const PaymentCard: React.FC<PaymentCardProps> = ({
    title,
    description,
    subTitle,
    isDisabled = false,
    isSelected = false,
    icon,
    onClick,
    isRecommended = false,
    dataTestId,
}) => {
    const [t] = useNewTranslation()
    return (
        <PaymentCardContainer
            isDisabled={isDisabled}
            isSelected={isSelected}
            onClick={onClick}
            data-testid={dataTestId}
        >
            <PaymentIcon type={icon} isDisabled={isDisabled} />
            <div className="content-container">
                <div className="content">
                    <div className="title">
                        <Text scale="bodyM700" color={isDisabled ? 'disable' : 'primary'}>
                            {title}
                        </Text>
                        {subTitle && (
                            <Text scale="bodyS500" color={isDisabled ? 'disable' : 'secondary'}>
                                {subTitle}
                            </Text>
                        )}
                        {isRecommended && (
                            <Tag>
                                <Text scale="caption500" color={'actionInverse'}>
                                    {t('tag.recommended')}
                                </Text>
                            </Tag>
                        )}
                    </div>

                    <Text scale="bodyS400" color={isDisabled ? 'disable' : 'secondary'}>
                        {description}
                    </Text>
                </div>
            </div>
        </PaymentCardContainer>
    )
}

type PaymentViewState = Pick<PaymentCardProps, 'isDisabled' | 'isSelected'>

const getBorderColor = (theme: Theme, config: PaymentViewState, defaultValue: string) => {
    const { isDisabled, isSelected } = config
    if (isDisabled) {
        return theme.colors.border.disable
    }

    if (isSelected) {
        return theme.colors.border.action
    }

    return defaultValue
}

const PaymentCardContainer = styled.div<PaymentViewState>`
    box-sizing: border-box;
    font-family: inherit;
    width: 264px;
    min-height: 148px;
    border-radius: 4px;
    background: ${({ theme }) => theme.colors.surface.primary};
    border: 2px solid
        ${({ theme, isSelected, isDisabled }) =>
            getBorderColor(theme, { isDisabled, isSelected }, theme.colors.border.primary)};
    padding: 12px;
    display: flex;
    gap: ${({ theme }) => theme.spacing[300]};
    flex-direction: column;

    &:hover {
        border-color: ${({ theme, isSelected, isDisabled }) =>
            getBorderColor(theme, { isDisabled, isSelected }, theme.colors.border.actionFocus)};
    }

    .content-container {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[300]};

        .title {
            display: flex;
            gap: ${({ theme }) => theme.spacing[100]};
        }

        .content {
            display: flex;
            flex: 1;
            gap: ${({ theme }) => theme.spacing[200]};
            flex-direction: column;
        }
    }
`

const Tag = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing[100]};
    border-radius: 4px;
    align-items: center;
    height: 24px;
    background-color: ${({ theme }) => theme.colors.surface.positive};
    padding: 0px 6px;
    width: fit-content;
    flex-shrink: 0;
`

export default PaymentCard
